import type {Suggestions} from '@github-ui/inline-autocomplete/types'
import {useQuery} from '@github-ui/react-query'
import {useMemo} from 'react'

import {useChatManager} from '../../utils/CopilotChatManagerContext'
import {ReferenceMention} from '../../utils/reference-mention'
import {RepositorySuggestion} from './RepositorySuggestion'

export interface RepositoriesQuery {
  category: 'repositories'
  nextCategory?: 'issues' | 'pulls' | 'discussions' | 'files'
  filter: string
}

export function useRepositoriesSuggestions(query: RepositoriesQuery | null) {
  const {service} = useChatManager()

  const repositoriesQuery = useQuery({
    enabled: query !== null,
    queryKey: ['copilot-autocomplete-repositories', query?.filter],
    queryFn: () => service.fetchAutocompleteRepositories(query?.filter ?? ''),
    placeholderData: prev => prev,
  })

  return useMemo<Suggestions | null>(() => {
    if (!query) return null
    const isMultistep = query.nextCategory !== undefined

    if (repositoriesQuery.isLoading) return 'loading'

    return (
      repositoriesQuery.data?.map(repo => ({
        value: isMultistep ? null : ReferenceMention.stringify({type: 'repository', repo: repo.nameWithOwner}),
        key: repo.nameWithOwner,
        render: props => (
          <RepositorySuggestion
            repository={repo}
            isMultistep={isMultistep}
            stale={repositoriesQuery.isPlaceholderData}
            {...props}
          />
        ),
      })) ?? null
    )
  }, [repositoriesQuery.isLoading, repositoriesQuery.isPlaceholderData, repositoriesQuery.data, query])
}
